// All 7 service definitions in priority order. Copy from Phase 3, augmented
// Feb 2026 with John's verified service guidance (repair size categories,
// standard screen-door pricing, PCA custom doors, Z-bar trim, condo notes).
//
// Disclaimer pattern used everywhere pricing is mentioned:
//   "approximately $X · final pricing confirmed after technician review"

const APPROX_DISCLAIMER =
    "Approximate guidance only. Final pricing confirmed after technician review.";

export const SERVICES = [
    {
        slug: "pool-cage-repair",
        name: "Pool Cage Repair",
        priority: true,
        h1: "Pool Cage Repair in Naples, FL",
        meta_title:
            "Pool Cage Repair Naples FL · Free Estimates · A1 Screening",
        meta_description:
            "Naples pool cage repair: torn screens, bent frames, storm damage, full rescreens. Free estimates. Family-owned and serving Southwest Florida since 1999. Call 239-434-7306.",
        primary_cta: "Get a Free Estimate",
        hero_summary:
            "Torn screens. Sagging panels. Bent frames. We've been repairing pool cages in Naples since 1999. Send us photos or call — we'll come look for free.",
        what_we_do: [
            "Torn or stretched screen panels — spot repairs or full panel replacements.",
            "Sun-faded or brittle screens — mesh that's reached the end of its life.",
            "Bent frames or broken hardware — posts, beams, super-gutters, brackets.",
            "Doors that won't close right — rollers, hinges, mesh, latches.",
            "Storm damage — cage damage after a hurricane or tropical storm. Availability may vary by schedule and season.",
        ],
        common_problems: [
            "One torn panel turning into three.",
            "No-see-ums getting through — wrong mesh or degraded mesh.",
            "Sagging beams or out-of-square frames.",
            "HOA letters about appearance.",
            "Storm damage you didn't catch right away.",
        ],
        process: [
            "You reach out — call, send photos, or fill the short estimate form.",
            "We come look — we inspect the cage in person and give you a free written estimate.",
            "You decide — no pressure, no upsell. We do what you approve.",
            "We get it done — clean work, neat frames, no mess left behind.",
        ],
        repair_or_rescreen: {
            repair: "One or two torn panels and the rest looks healthy? A spot repair is usually enough.",
            rescreen:
                "Multiple torn panels, or screens that look faded and brittle? A full rescreen is usually the better long-term choice.",
            structural:
                "Frame issues on top of screen damage? Structural repair plus rescreen — handled in one visit when possible.",
        },
        // Feb 2026 — John's verified repair size categories
        repair_size_categories: {
            heading: "Small, Medium, or Large Repair?",
            intro:
                "Some homeowners only need a few panels replaced. Others may prefer a full rescreen if the screen is older, brittle, or damaged in multiple areas. A1 Screening can explain the options and let you choose what makes the most sense.",
            categories: [
                {
                    label: "Small repair",
                    panels: "1–3 panels",
                    description:
                        "When the rest of the cage is in good shape, replacing a few panels is usually enough.",
                },
                {
                    label: "Medium repair",
                    panels: "4–10 panels",
                    description:
                        "We'll explain both repair and rescreen options so you can choose based on age, condition, and budget.",
                },
                {
                    label: "Large repair",
                    panels: "approximately 11–20 panels",
                    description:
                        "Some homeowners still choose panel-by-panel repair. Others find a full rescreen makes more sense.",
                },
            ],
            cautious_footer:
                "We provide options, not pressure. Final recommendations and pricing are confirmed after technician review.",
        },
        // AEO direct-answer blocks — short, conversational, cautious
        aeo_answers: [
            {
                q: "What is considered a small screen repair?",
                a: "A small screen repair is usually 1–3 panels — a good fit when the rest of the cage is still in good condition.",
            },
            {
                q: "Should I repair panels or rescreen the whole pool cage?",
                a: "If only 1–3 panels are damaged, repair may be enough. If several panels are damaged, brittle, or aging, A1 Screening can explain both repair and full rescreen options so you can choose what makes the most sense.",
            },
            {
                q: "Does screen panel height affect price?",
                a: "Yes. Roof panels over the pool, or panels above what a 24-foot ladder can safely reach, may need special review. Most other panels are fairly close in price per square foot.",
            },
        ],
        faqs: [
            {
                q: "Who repairs pool cages in Naples, FL?",
                a: "A1 Screening repairs pool cages in Naples and across Southwest Florida. Family-owned since 1999. Call 239-434-7306 or request a free estimate online.",
            },
            {
                q: "How do I know if my pool cage needs repair?",
                a: "If you see torn or sagging screens, bugs getting through, bent frames, or doors that won't close right, it's time for a repair. If you're unsure, send us a few photos and we'll tell you what we see.",
            },
            {
                q: "What counts as a small pool cage screen repair?",
                a: "A small screen repair is usually 1–3 panels. A1 Screening can replace a few damaged panels when the rest of the pool cage or lanai screen is still in good condition.",
            },
            {
                q: "What counts as a medium screen repair?",
                a: "A medium repair is usually 4–10 panels. At that point, A1 Screening may explain both repair and rescreening options so the customer can choose what makes the most sense.",
            },
            {
                q: "What counts as a large screen repair?",
                a: "A large repair is usually around 11–20 panels. Depending on the age and condition of the screen, some homeowners may choose panel repair while others may consider a full rescreen.",
            },
            {
                q: "Should I repair panels or rescreen the whole pool cage?",
                a: "If only 1–3 panels are damaged, repair may be enough. If several panels are damaged, brittle, or aging, A1 Screening can explain both repair and full rescreening options so you can choose what makes the most sense.",
            },
            {
                q: "Does the location of the screen panel affect price?",
                a: "Yes. The height, location, and accessibility of the panel can affect pricing. Roof panels over the pool or panels above what a 24-foot ladder can safely reach may require special review.",
            },
            {
                q: "Can torn pool cage screens be replaced?",
                a: "Yes — torn panels can usually be replaced one at a time. If multiple panels are torn or the mesh is brittle from sun, a full rescreen is usually the better long-term choice.",
            },
            {
                q: "Do you repair storm-damaged pool cages?",
                a: "Yes — we repair storm-damaged pool cages and screens across Naples and Southwest Florida. Storm repair availability may vary by schedule and season.",
            },
            {
                q: "How long does pool cage repair take?",
                a: "Most small repairs in Naples can be completed in a single visit. Full rescreens typically take longer depending on cage size and weather. Storm and structural repairs may need additional time. We'll give you a clear timeline as part of your free estimate.",
            },
        ],
    },
    {
        slug: "pool-rescreening",
        name: "Pool Rescreening",
        h1: "Pool Cage Rescreening in Naples, FL",
        meta_title:
            "Pool Rescreening Naples FL · Full Cage Rescreens · A1 Screening",
        meta_description:
            "Full pool cage rescreening in Naples and Southwest Florida. Free estimates. Bug-tight mesh, clean workmanship. Family-owned since 1999. Call 239-434-7306.",
        primary_cta: "Ask About Pool Rescreening",
        hero_summary:
            "When spot repairs aren't enough, a full rescreen brings your pool cage back to like-new. We rescreen pool cages across Naples and Southwest Florida — clean, tight, and built for our climate.",
        what_we_do: [
            "Remove old, brittle, or torn mesh.",
            "Inspect frames, hardware, and super-gutters as we go.",
            "Install new mesh sized and tensioned for Southwest Florida's sun, wind, and bugs.",
            "Replace spline and any worn small hardware.",
            "Clean up after ourselves so you wouldn't know we'd been there — except the cage looks new.",
        ],
        common_problems: [
            "Mesh feels brittle, dusty, or crumbles when you touch it.",
            "Multiple panels are torn or stretched.",
            "Color has faded badly (gray-white or yellowed).",
            "No-see-ums or small bugs are getting through everywhere.",
            "A previous storm caused damage across more than one section.",
        ],
        process: [
            "You reach out — call, send photos, or fill the short estimate form.",
            "We inspect the cage and give you a free written estimate with both options (spot repair vs. full rescreen).",
            "You decide. We don't upsell.",
            "We rescreen the cage cleanly and leave the area tidy.",
        ],
        // Repair-vs-rescreen guidance (Feb 2026 — John's verified service guidance)
        repair_or_rescreen: {
            repair:
                "If only 1–3 panels are damaged, a panel-by-panel repair is often the right call.",
            rescreen:
                "If 4–10 panels are damaged, or the mesh is faded and brittle across the cage, a full rescreen is usually worth considering.",
            customer_choice:
                "The customer makes the final choice. A1 Screening provides options, not pressure.",
        },
        aeo_answers: [
            {
                q: "Should I rescreen the whole pool cage or repair it?",
                a: "Repair may make sense for 1–3 damaged panels. Medium and large panel counts (4–10 or 11–20) may make a full rescreen worth considering. The customer makes the final choice — A1 Screening provides options, not pressure.",
            },
        ],
        faqs: [
            {
                q: "How much does pool cage rescreening cost in Naples?",
                a: "Pool cage rescreening costs in Naples depend on the size of the enclosure, number of panels, screen material, access, storm damage, and whether structural repairs are needed. A1 Screening provides free estimates so homeowners can get accurate pricing for their specific pool cage or lanai.",
            },
            {
                q: "Do I need full rescreening or just a repair?",
                a: "If you have one or two torn panels, a spot repair is usually enough. If your screens are sun-faded, brittle, or torn in multiple panels, a full rescreen is usually the better long-term choice.",
            },
            {
                q: "How long does pool rescreening take?",
                a: "It depends on the size of the cage and the weather. We'll give you a clear timeline as part of your free estimate.",
            },
            {
                q: "What makes pool screens wear out in Southwest Florida?",
                a: "Mostly sun, salt air, wind, storms, pets, and pool chemistry over time. Naples conditions are tougher on screen mesh than most other parts of the state.",
            },
            {
                q: "Can I get a free pool rescreening estimate?",
                a: "Yes — every estimate is free. Call 239-434-7306 or fill out the short estimate form and we'll come take a look.",
            },
        ],
    },
    {
        slug: "structural-pool-cage-repair",
        name: "Structural Pool Cage Repair",
        h1: "Structural Pool Cage Repair in Naples, FL",
        meta_title:
            "Structural Pool Cage Repair Naples · Frames, Posts, Hardware · A1 Screening",
        meta_description:
            "Structural pool cage repair in Naples — bent frames, broken posts, super-gutters, hardware. Free estimates. 26+ years of Southwest Florida experience.",
        primary_cta: "Get a Free Estimate",
        hero_summary:
            "Bent frames, broken posts, sagging cages — we've fixed it all since 1999.",
        what_we_do: [
            "Bent or out-of-square frames.",
            "Broken or rusted posts and beams.",
            "Damaged super-gutters and hardware.",
            "Loose anchors and cracked welds.",
            "Storm-related structural damage.",
        ],
        common_problems: [
            "Cage is sagging or out of square.",
            "Posts feel loose or cracked.",
            "Super-gutter is leaking or pulled away.",
            "Frame damage after a tropical storm.",
        ],
        process: [
            "Send us photos so we can see what we're dealing with.",
            "We inspect in person and give you a free written estimate.",
            "Repair vs. rebuild — we'll tell you straight.",
            "Clean structural work that lasts in SWFL conditions.",
        ],
        faqs: [
            {
                q: "Can a bent or damaged pool cage frame be repaired?",
                a: "Yes — most bent frames, broken posts, and damaged hardware can be repaired. We'll inspect and tell you whether it's a repair or a rebuild.",
            },
            {
                q: "Do I need to rebuild my pool cage or repair it?",
                a: "It depends on the extent of structural damage and the age of the cage. We give honest assessments — sometimes a clean repair lasts another decade; sometimes a rebuild is the smarter call.",
            },
        ],
    },
    {
        slug: "lanai-screen-repair",
        name: "Lanai Screen Repair",
        h1: "Lanai Screen Repair in Naples, FL",
        meta_title: "Lanai Screen Repair Naples FL · A1 Screening",
        meta_description:
            "Lanai screen repair in Naples and Southwest Florida. Torn screens, frame repairs, full lanai rescreens. Free estimates. Family-owned since 1999.",
        primary_cta: "Schedule Screen Repair",
        hero_summary:
            "Bugs getting through your lanai? We'll get it tight again.",
        what_we_do: [
            "Torn or stretched lanai panels.",
            "Frame repairs and full lanai rescreens.",
            "Mesh upgraded for SWFL no-see-ums.",
            "Lanai screen door tune-ups.",
        ],
        common_problems: [
            "Bugs getting through despite intact-looking screens.",
            "Sun-faded mesh that snaps when handled.",
            "Lanai door that drags or won't close.",
        ],
        process: [
            "You reach out — call or send photos.",
            "We inspect and give you a free estimate.",
            "Quick turnaround on most lanai jobs.",
            "Clean work, neat frames.",
        ],
        faqs: [
            {
                q: "Who repairs lanai screens in Naples?",
                a: "A1 Screening repairs lanai screens in Naples and across Southwest Florida — torn panels, frames, screen doors, and full lanai rescreens.",
            },
            {
                q: "Can lanai screens be repaired panel by panel?",
                a: "Usually yes. If the rest of the mesh is healthy, panel-by-panel repair is the right move.",
            },
            {
                q: "Do you fix the screen door on my lanai?",
                a: "Yes — sliding doors, swing doors, rollers, hinges, mesh, and frames. Often a single visit.",
            },
        ],
    },
    {
        slug: "screen-door-repair",
        name: "Screen Door Repair",
        h1: "Screen Door Repair in Naples, FL",
        meta_title:
            "Screen Door Repair Naples FL · Sliding & Swing Doors · A1 Screening",
        meta_description:
            "Screen door repair and replacement in Naples — sliding doors, swing doors, rollers, hinges, mesh, custom PCA doors. Free estimates. Family-owned since 1999.",
        primary_cta: "Schedule Screen Repair",
        hero_summary:
            "Door sticking, rollers gone, or mesh torn? One visit, fixed.",
        what_we_do: [
            "Sliding screen doors — rollers, tracks, mesh, frames.",
            "Swing screen doors — hinges, latches, mesh.",
            "Standard screen door replacement (~36\" × 80\").",
            "Custom screen doors — certified approved installer for PCA Products.",
            "Z-bar trim replacement.",
            "Full replacement when repair won't hold.",
        ],
        common_problems: [
            "Door drags on the track.",
            "Rollers worn or popped off.",
            "Mesh torn at the bottom.",
            "Door won't latch closed.",
            "Z-bar trim around the door is loose, bent, or rusted.",
        ],
        process: [
            "Tell us what's wrong (photo helps).",
            "We bring parts to fix it in one visit when possible.",
            "Clean install, smooth slide, tight mesh.",
        ],
        // Feb 2026 — John's verified screen-door pricing guidance
        pricing_guidance: {
            heading: "Screen Door Pricing Guidance",
            intro:
                "These are approximate ranges so you have a sense of what to expect. Final pricing is confirmed after technician review.",
            items: [
                {
                    label: "Standard 36\" × 80\" screen door replacement",
                    approx: "approximately $275",
                    includes:
                        "Includes removing the old door, installing the new door, new screen, hinges, handles, closing plunger, and bottom bug sweep.",
                    disclaimer:
                        "Final pricing may vary based on job conditions and technician review.",
                },
                {
                    label: "Z-bar trim replacement (if needed)",
                    approx: "may add approximately $125",
                    includes:
                        "Z-bar trim is the trim around the edge of the door. If it's loose, bent, or rusted, it sometimes needs to be replaced when we install the new door.",
                    disclaimer:
                        "Final pricing may vary based on job conditions and technician review.",
                },
            ],
            custom_doors: {
                heading: "Custom Screen Doors — PCA Products",
                body:
                    "A1 Screening is a certified approved installer for PCA Products custom screen doors. Customers can explore custom screen door styles, sizes, and design options at pcaproducts.com, then contact A1 Screening for installation guidance and technician-reviewed pricing.",
                vendor_label: "Explore options at pcaproducts.com",
                vendor_url: "https://pcaproducts.com",
                disclaimer:
                    "Final options and pricing are confirmed after technician review.",
            },
            footer: APPROX_DISCLAIMER,
        },
        aeo_answers: [
            {
                q: "How much is a standard screen door replacement?",
                a: "A standard 36\" × 80\" screen door replacement is approximately $275. This typically includes removing the old door, installing the new door, new screen, hinges, handles, closing plunger, and bottom bug sweep. Final pricing may vary based on job conditions and technician review.",
            },
            {
                q: "Do you install custom screen doors?",
                a: "Yes. A1 Screening is a certified approved installer for PCA Products custom screen doors. Explore options at pcaproducts.com, then contact A1 Screening for installation guidance and technician-reviewed pricing.",
            },
            {
                q: "What is Z-bar trim?",
                a: "Z-bar trim is the trim around the edge of a screen door. If it needs to be replaced, it may add approximately $125 depending on the job.",
            },
        ],
        faqs: [
            {
                q: "Who replaces sliding screen doors in Naples?",
                a: "A1 Screening — we replace and repair sliding and swing screen doors across Naples and Southwest Florida. Family-owned since 1999.",
            },
            {
                q: "Can a stuck screen door be fixed?",
                a: "Most stuck screen doors come down to worn rollers or a bent track. We can usually fix it in a single visit.",
            },
            {
                q: "How much does a standard screen door replacement cost?",
                a: "A standard 36\" × 80\" screen door replacement is approximately $275. This typically includes removing the old door, installing the new door, new screen, hinges, handles, closing plunger, and bottom bug sweep. Final pricing may vary based on job conditions and technician review.",
            },
            {
                q: "Do you offer custom screen doors?",
                a: "Yes. A1 Screening is a certified approved installer for PCA Products custom screen doors. Customers can explore custom screen door styles, sizes, and design options at pcaproducts.com, then contact A1 Screening for installation guidance and technician-reviewed pricing.",
            },
            {
                q: "What is Z-bar trim on a screen door?",
                a: "Z-bar trim is the trim around the edge of the screen door. If it needs to be replaced, it may add approximately $125 depending on the job.",
            },
        ],
    },
    {
        slug: "storm-screen-repair",
        name: "Storm Screen Repair",
        h1: "Storm Damage Pool Cage & Screen Repair in Naples, FL",
        meta_title:
            "Storm Screen Repair Naples FL · Pool Cage Storm Damage · A1 Screening",
        meta_description:
            "Storm-damaged pool cage and screen repair in Naples and Southwest Florida. Free estimates. Storm repair availability may vary by schedule and season.",
        primary_cta: "Request Storm Damage Repair",
        hero_summary:
            "After the storm, your pool cage shouldn't wait. We'll come look — for free. Storm repair availability may vary by schedule and season.",
        what_we_do: [
            "Torn screens after wind damage.",
            "Bent frames, broken posts, broken hardware.",
            "Super-gutter and structural damage.",
            "Full-cage rescreens after storms.",
        ],
        common_problems: [
            "Multiple panels torn out by wind.",
            "Bent frame corners.",
            "Hardware sheared off.",
            "Hidden damage that didn't show until later.",
        ],
        process: [
            "Take photos of the damage — they help us a lot.",
            "Stay clear of any sagging beams.",
            "Call 239-434-7306 to get on the schedule.",
            "We give you a free written estimate.",
        ],
        faqs: [
            {
                q: "Can storm-damaged pool screens be repaired?",
                a: "Yes — most storm-damaged pool cages and screens can be repaired or rescreened. Storm repair availability may vary by schedule and season. Call 239-434-7306 to get on the list.",
            },
            {
                q: "Does insurance cover pool cage damage from a hurricane?",
                a: "It depends on your specific homeowners policy and deductible. Check with your insurer first — they can tell you what your policy covers and what documentation you'll need.",
            },
        ],
    },
    {
        slug: "condo-screen-repair",
        name: "Condo Screen Repair",
        h1: "Condo Screen Repair in Naples, FL",
        meta_title:
            "Condo Screen Repair Naples FL · Lanais, Doors, Patios · A1 Screening",
        meta_description:
            "Condo screen repair in Naples — lanais, balconies, screen doors, mesh. Property-manager-friendly. Free estimates. Family-owned since 1999.",
        primary_cta: "Get a Free Estimate",
        hero_summary:
            "From pool cages to condos, we screen it all.",
        what_we_do: [
            "Condo lanai and balcony screen repairs.",
            "Sliding and swing screen doors.",
            "Full lanai rescreens for condos.",
            "Property-manager-friendly scheduling.",
        ],
        common_problems: [
            "Lanai or balcony screens torn.",
            "HOA appearance compliance.",
            "Coordinating with renters or seasonal residents.",
        ],
        process: [
            "Property manager or owner contacts us.",
            "We coordinate with the tenant directly when needed.",
            "Free written estimate.",
            "Clean work, photo updates when requested.",
        ],
        // Feb 2026 — John's verified condo pricing caveats
        pricing_caveats: {
            heading: "Condo Pricing Notes",
            body:
                "Condo screen repair pricing can vary based on floor level, building access, height, and job conditions. A third-floor apartment is different from a high-rise unit on the 12th floor, and condo work may be priced differently than a single-family home patio or pool cage. A1 Screening provides fair pricing for quality work and confirms final pricing after technician review.",
            factors: [
                "Floor level (3rd floor vs. high-rise)",
                "Building access and elevator availability",
                "Height and exterior reach",
                "HOA / building rules",
                "Job conditions",
            ],
            disclaimer:
                "Final pricing for condo screen repair is confirmed after technician review. We do not publish exact condo pricing online.",
        },
        aeo_answers: [
            {
                q: "Do condos cost more for screen repair?",
                a: "Condo screen repair pricing can vary based on floor level, access, height, building rules, and job conditions. A third-floor unit is different from a high-rise unit, so final pricing is confirmed after technician review.",
            },
        ],
        faqs: [
            {
                q: "Do you repair condo screens in Naples?",
                a: "Yes — condo lanais, balconies, and screen doors. Property-manager-friendly. Family-owned since 1999.",
            },
            {
                q: "Do you work with property managers and HOAs?",
                a: "Yes — we coordinate directly with property managers and HOA-aware processes across Naples and Southwest Florida.",
            },
            {
                q: "Is condo screen repair priced differently?",
                a: "Yes. Condo screen repair pricing can vary depending on the floor level, access, height, building rules, and job conditions. A third-floor unit is different from a high-rise unit, so final pricing is confirmed after technician review.",
            },
        ],
    },
];

export const SERVICE_BY_SLUG = SERVICES.reduce((acc, s) => {
    acc[s.slug] = s;
    return acc;
}, {});
